import React, { useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import ArrowRightIcon from '@mui/icons-material/ArrowForward';
import ArrowLeftIcon from '@mui/icons-material/ArrowBack';
import { AutoCompleteField, Dropdown } from '../../components/widgets';
import Tooltip from '../../components/molecules/Tooltip';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import { CustomMetricsContext } from '../context';
import { TRANSLATION_PREFIX, getDefaultResult } from '../consts';
import { refTableHasEventColumn } from '../utils';
import { filterByLabelOnly } from './selectFilter';
import css from '../page.css';

const FILTER_METRIC_PLACEHOLDER = `${TRANSLATION_PREFIX}.PLACEHOLDERS.FILTER_METRIC`;
const BLANK_OPERATOR_OPTION_NAME = 'default';

const resultErrorsShape = PropTypes.shape({
    metric: PropTypes.bool,
    metricCollision: PropTypes.bool,
    opValue: PropTypes.bool,
    sumRequiresRefTable: PropTypes.bool,
});

const getValueDisplay = value => {
    if (value === null || value === undefined) {
        return '';
    }

    return typeof value === 'object' ? value.display_name : `${value}`;
};

function ResultRow({ result, onChange, translate, errors = null, showErrors = false, sumOverEvents = false }) {
    const { visibleMetrics, allResultValues, resultOperators } = useContext(CustomMetricsContext);
    // Remount the value autocomplete after a selection so it re-reads the (object) value;
    // typing must not remount (it would lose focus), hence a counter and not the value itself.
    const [valueSelectionCount, setValueSelectionCount] = useState(0);

    // AutoCompleteField captures its onInputChange prop once per mount, so the typed-value
    // handler must read the latest result/onChange through refs instead of its closure.
    const resultRef = useRef(result);
    resultRef.current = result;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const metricOptions = visibleMetrics.map(metric => ({ value: metric.name, label: metric.display_name }));
    // Sum mode sums a table's per-event payouts, so a plain metric is not a valid choice there -- offering
    // one would only lead to the sumRequiresRefTable error (and APIInvalidCpeSumRule on the backend).
    // Narrowed again to tables declaring an event column, since only those can price rows. The rule's own
    // table is always kept: tables configured before that column existed would otherwise drop out of the
    // options and blank a working rule on the next save.
    const selectableValues = sumOverEvents
        ? allResultValues.filter(
              value => value.isRefTable && (refTableHasEventColumn(value) || value.name === result.value?.name)
          )
        : allResultValues;
    const valueOptions = selectableValues.map(value => ({ value: value.name, label: value.display_name }));
    const operatorItems = resultOperators.map(operator => ({
        ...operator,
        name: operator.name || BLANK_OPERATOR_OPTION_NAME,
        display_name: operator.operator,
    }));
    const selectedOperator = operatorItems.find(
        ({ name }) => name === (result.operator.name || BLANK_OPERATOR_OPTION_NAME)
    );

    const onMetricSelected = option => {
        const metric = visibleMetrics.find(({ name }) => name === option.value) || getDefaultResult().metric;
        onChange({ ...result, metric });
    };

    const onOperatorSelected = operatorItem => {
        const operator = resultOperators.find(({ name }) => name === operatorItem.name) || resultOperators[0];
        onChange({ ...result, operator });
    };

    const onValueSelected = option => {
        const value = allResultValues.find(({ name }) => name === option.value);
        setValueSelectionCount(count => count + 1);
        onChange({ ...result, value, isRefTable: !!value?.isRefTable });
    };

    const onValueTyped = text => {
        onChangeRef.current({ ...resultRef.current, value: text, isRefTable: false });
    };

    const onToggleOpValue = () => {
        if (result.showOpValue) {
            const defaultResult = getDefaultResult();
            onChange({
                ...result,
                showOpValue: false,
                _previous: { operator: result.operator, value: result.value, isRefTable: result.isRefTable },
                operator: defaultResult.operator,
                value: defaultResult.value,
                isRefTable: false,
            });
        } else {
            onChange({
                ...result,
                showOpValue: true,
                ...(result._previous || {}),
            });
        }
    };

    const refTableLink = result.isRefTable && typeof result.value === 'object' && result.value !== null && (
        <Tooltip titleTranslationKey={`${TRANSLATION_PREFIX}.OPEN_REFERENCE_TABLE`}>
            <a
                className={css.refTableLink}
                href={`#/reference/${result.value.slug_name || result.value.name}`}
                target="_blank"
                rel="noreferrer"
                aria-label={translate(`${TRANSLATION_PREFIX}.OPEN_REFERENCE_TABLE`)}
            >
                <Icon name="table" size={IconSizes.SMALL} variant={IconVariants.ACTIVE} />
            </a>
        </Tooltip>
    );

    // Selected values must be option objects: displaying them through a controlled input
    // string makes them vanish whenever react-select hides its input (menu toggle, blur).
    const isObjectValue = typeof result.value === 'object' && result.value !== null;
    const selectedMetricOption = result.metric?.name
        ? { value: result.metric.name, label: result.metric.display_name }
        : null;

    // In sum mode the operand and operator are inert -- the table supplies a per-event operand for each of
    // its rows -- so rendering them as "<metric> x <table>" would describe a multiplication that never
    // happens. They stay in state (serialization still emits the triple) but are replaced by static text.
    if (sumOverEvents) {
        return (
            <div>
                <div className={css.resultRow}>
                    <span className={css.sumModeOperand}>
                        <Translate id={`${TRANSLATION_PREFIX}.SUM_MODE_OPERAND`} />
                    </span>
                    <div className={css.resultField}>
                        <AutoCompleteField
                            key={`value-${valueSelectionCount}`}
                            isMulti={false}
                            searchable
                            controlled={!isObjectValue}
                            options={valueOptions}
                            value={
                                isObjectValue
                                    ? { value: result.value.name, label: result.value.display_name }
                                    : getValueDisplay(result.value)
                            }
                            placeholder={FILTER_METRIC_PLACEHOLDER}
                            onChange={onValueSelected}
                            onInputChange={onValueTyped}
                            debounceTime={0}
                            selectOptions={{ filterOption: filterByLabelOnly }}
                        />
                    </div>
                    {refTableLink}
                </div>
                {showErrors && errors?.sumRequiresRefTable && (
                    <div className={css.errorText}>
                        <Translate id={`${TRANSLATION_PREFIX}.SUM_RULE_REQUIRES_REF_TABLE`} />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div>
            <div className={css.resultRow}>
                <div className={css.resultField}>
                    <AutoCompleteField
                        key={`metric-${result.metric?.name || ''}`}
                        isMulti={false}
                        searchable
                        options={metricOptions}
                        value={selectedMetricOption}
                        placeholder={FILTER_METRIC_PLACEHOLDER}
                        onChange={onMetricSelected}
                        selectOptions={{ filterOption: filterByLabelOnly }}
                        error={
                            showErrors && errors?.metric
                                ? 'STATIC.REQUIRED_FIELD'
                                : showErrors && errors?.metricCollision
                                ? 'STATIC.FIELD_COLLISION'
                                : ''
                        }
                    />
                </div>
                {result.showOpValue && (
                    <div className={css.resultOperator}>
                        <Dropdown
                            items={operatorItems}
                            selected={selectedOperator}
                            onSelection={onOperatorSelected}
                            disabled={!result.metric?.name}
                            containerStyle={{ width: '100%' }}
                            wrapperStyle={{ width: '100%' }}
                        />
                    </div>
                )}
                {result.showOpValue && (
                    <div className={css.resultField}>
                        <AutoCompleteField
                            key={`value-${valueSelectionCount}`}
                            isMulti={false}
                            searchable
                            controlled={!isObjectValue}
                            options={valueOptions}
                            value={
                                isObjectValue
                                    ? { value: result.value.name, label: result.value.display_name }
                                    : getValueDisplay(result.value)
                            }
                            placeholder={FILTER_METRIC_PLACEHOLDER}
                            onChange={onValueSelected}
                            onInputChange={onValueTyped}
                            debounceTime={0}
                            disabled={!result.metric?.name}
                            selectOptions={{ filterOption: filterByLabelOnly }}
                        />
                    </div>
                )}
                {result.showOpValue && refTableLink}
                <button
                    type="button"
                    className={css.opValueToggle}
                    title={result.showOpValue ? 'Remove operator and value' : 'Add operator and value'}
                    onClick={onToggleOpValue}
                >
                    {result.showOpValue ? <ArrowLeftIcon /> : <ArrowRightIcon />}
                </button>
            </div>
            {showErrors && errors?.opValue && (
                <div className={css.errorText}>
                    <Translate id={`${TRANSLATION_PREFIX}.BOTH_FIELDS_REQUIRED`} />
                </div>
            )}
        </div>
    );
}

ResultRow.propTypes = {
    result: PropTypes.shape({
        metric: PropTypes.objectOf(PropTypes.any),
        operator: PropTypes.objectOf(PropTypes.any),
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
        isRefTable: PropTypes.bool,
        showOpValue: PropTypes.bool,
        _previous: PropTypes.objectOf(PropTypes.any),
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
    errors: resultErrorsShape,
    showErrors: PropTypes.bool,
    sumOverEvents: PropTypes.bool,
};

export default withLocalize(ResultRow);
